import type { ReactNode } from 'react';
import { Card, CardContent } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { InvoicingDealerSection } from './InvoicingDealerSection';
import { InvoiceSelectionSection } from './InvoiceSelectionSection';
import { InvoiceOverBalanceSection } from './InvoiceOverBalanceSection';
import { InvoiceDetailsSection } from './InvoiceDetailsSection';
import { AGREEMENT_INACTIVE_REASON } from '../utils/invoiceSubmitGates';
import type { InvoicingDealerSectionProps } from './InvoicingDealerSection';
import type { InvoiceSelectionSectionProps } from './InvoiceSelectionSection';
import type { InvoiceOverBalanceSectionProps } from './InvoiceOverBalanceSection';
import type { InvoiceDetailsSectionProps } from './InvoiceDetailsSection';
import type { PanelSectionId } from '../utils/invoicePanel';

export interface InvoicePanelProps {
    sections: PanelSectionId[];
    successNotice: ReactNode;
    dealer: InvoicingDealerSectionProps;
    selection: InvoiceSelectionSectionProps;
    overBalance: InvoiceOverBalanceSectionProps;
    details: InvoiceDetailsSectionProps;
}

const GUARDED_SECTIONS: readonly PanelSectionId[] = ['dealer', 'over_balance', 'details'];

function renderSection(id: PanelSectionId, props: InvoicePanelProps): ReactNode {
    switch (id) {
        case 'inactive':
            return (
                <p key={id} role="note" className="text-sm text-mp-foreground">
                    {AGREEMENT_INACTIVE_REASON}
                </p>
            );
        case 'success':
            return <div key={id}>{props.successNotice}</div>;
        case 'dealer':
            return <InvoicingDealerSection key={id} {...props.dealer} />;
        case 'selection':
            return <InvoiceSelectionSection key={id} {...props.selection} />;
        case 'over_balance':
            return <InvoiceOverBalanceSection key={id} {...props.overBalance} />;
        case 'details':
            return <InvoiceDetailsSection key={id} {...props.details} />;
    }
}

const ADD_INVOICE = { resource: 'invoice', action: 'add' } as const;

export function InvoicePanel(props: InvoicePanelProps) {
    return (
        <Card>
            <CardContent className="space-y-4">
                {props.sections.map((id) =>
                    GUARDED_SECTIONS.includes(id) ? (
                        <PermissionGuard key={id} permission={ADD_INVOICE}>
                            {renderSection(id, props)}
                        </PermissionGuard>
                    ) : (
                        renderSection(id, props)
                    ),
                )}
            </CardContent>
        </Card>
    );
}
